
import React, { useState } from 'react';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { useUserDashboardData } from '@/hooks/useUserDashboardData';
import { usePageContext } from '@/hooks/usePageContext';
import { useToast } from '@/components/ui/use-toast';
import SystemSettings from '@/components/admin/SystemSettings';
import DeviceSettings from '@/components/admin/DeviceSettings';
import LocationSettings from '@/components/admin/LocationSettings';
import SlideSettings from '@/components/admin/SlideSettings';
import { Loader2 } from 'lucide-react';

const Settings: React.FC = () => {
  const { toast } = useToast();
  const { isLoading, user, settings } = useUserDashboardData();
  const { 
    pages, 
    setPages,
    titleConfig,
    setTitleConfig,
    deviceSpecificSettings,
    setDeviceSpecificSettings,
    rotationInterval,
    setRotationInterval,
    autoScroll,
    setAutoScroll
  } = usePageContext();
  const [activeTab, setActiveTab] = useState("general");
  
  // Handle edit page navigation
  const handleEditPage = (pageId: string) => {
    // Navigate to page editor with the selected page ID
    console.log(`Navigating to edit page: ${pageId}`);
    // This would typically use react-router-dom navigation
  };
  
  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-screen">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
        <span className="ml-2">Loading settings...</span>
      </div>
    );
  }

  return (
    <div className="container mx-auto p-6">
      <h1 className="text-3xl font-bold mb-6">הגדרות מערכת</h1>
      
      <Tabs 
        defaultValue="general" 
        value={activeTab}
        onValueChange={setActiveTab}
        className="space-y-4"
      >
        <TabsList className="grid grid-cols-5 w-full">
          <TabsTrigger value="general">כללי</TabsTrigger>
          <TabsTrigger value="slides">שקופיות</TabsTrigger>
          <TabsTrigger value="device">מכשירים</TabsTrigger>
          <TabsTrigger value="location">מיקום</TabsTrigger>
          <TabsTrigger value="user">משתמש</TabsTrigger>
        </TabsList>

        <TabsContent value="general">
          <Card>
            <CardHeader>
              <CardTitle>הגדרות כלליות</CardTitle>
            </CardHeader>
            <CardContent>
              <SystemSettings 
                titleConfig={titleConfig}
                setTitleConfig={setTitleConfig}
              />
            </CardContent>
          </Card>
        </TabsContent>
        
        <TabsContent value="slides">
          <SlideSettings 
            pages={pages}
            setPages={setPages}
            onEditPage={handleEditPage}
            rotationInterval={rotationInterval}
            setRotationInterval={setRotationInterval}
            autoScroll={autoScroll}
            setAutoScroll={setAutoScroll}
          />
        </TabsContent>
        
        <TabsContent value="device">
          <DeviceSettings 
            deviceSpecificSettings={deviceSpecificSettings}
            setDeviceSpecificSettings={setDeviceSpecificSettings}
          />
        </TabsContent>
        
        <TabsContent value="location">
          <LocationSettings />
        </TabsContent>
        
        <TabsContent value="user">
          <Card>
            <CardHeader>
              <CardTitle>פרופיל משתמש</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <h3 className="text-lg font-medium">שם משתמש</h3>
                <p>{user?.email}</p>
              </div>
              <div>
                <h3 className="text-lg font-medium">תפקיד</h3>
                <p>{user?.role || 'משתמש רגיל'}</p>
              </div>
              <div>
                <h3 className="text-lg font-medium">הגדרות נשמרות</h3>
                <div className="bg-secondary/20 p-4 rounded-md">
                  <pre className="text-xs overflow-auto">
                    {Object.keys(settings).length > 0 
                      ? JSON.stringify(settings, null, 2) 
                      : 'אין הגדרות שמורות'}
                  </pre>
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default Settings;
